import React, { useEffect, useMemo, useState } from "react";
import Button from "../../components/UI/Buttons/Button/Button";
import SimpleModal from "../../components/UI/Modals/SimpleModal/SimpleModal";
import NotificationMessage from "../../components/UI/NotificationMessage/NotificationMessage";
import {
  VOLUNTEER_DAYS,
  VOLUNTEER_DAY_LABELS,
  VolunteerDayOfWeek,
  VolunteerWorkingHoursEntry,
} from "../../types/volunteerProfile.types";
import styles from "./WorkingHoursEditor.module.scss";

// ITAM-2677: seven named rows, a per-day checkbox plus a pair of <input
// type="time"> (minute granularity, serialized "HH:mm"; seconds are a backend
// 400). Off days are omitted from the payload entirely: a missing row means
// unavailable that day.
//
// Validation: `from < until` STRICTLY. The backend's isAvailableNow() treats a
// row with `from >= until` as never available and performs no interval
// validation itself, so equal times and overnight intervals are rejected at
// input; they are dead windows, not alternative ones.

export interface DayRow {
  enabled: boolean;
  from: string;
  until: string;
}

export type HoursDraft = Record<VolunteerDayOfWeek, DayRow>;

const EMPTY_ROW: DayRow = { enabled: false, from: "08:00", until: "16:00" };

export const buildHoursDraft = (
  entries: VolunteerWorkingHoursEntry[] | null | undefined
): HoursDraft => {
  const draft = {} as HoursDraft;
  VOLUNTEER_DAYS.forEach((day) => {
    draft[day] = { ...EMPTY_ROW };
  });
  (entries ?? []).forEach((entry) => {
    if (!VOLUNTEER_DAYS.includes(entry.dayOfWeek)) {
      return;
    }
    // The backend accepts duplicate days and picks the FIRST; mirror that so
    // the editor shows the window actually in use.
    if (draft[entry.dayOfWeek].enabled) {
      return;
    }
    draft[entry.dayOfWeek] = {
      enabled: true,
      // Tolerate an "HH:mm:ss" the backend might yet return; the input needs
      // exactly "HH:mm".
      from: (entry.availableFrom ?? "").slice(0, 5),
      until: (entry.availableUntil ?? "").slice(0, 5),
    };
  });
  return draft;
};

export const hoursDraftToEntries = (
  draft: HoursDraft
): VolunteerWorkingHoursEntry[] =>
  VOLUNTEER_DAYS.filter((day) => draft[day].enabled).map((day) => ({
    dayOfWeek: day,
    availableFrom: draft[day].from,
    availableUntil: draft[day].until,
  }));

/**
 * Accepts "HH:mm" and the "HH:mm:ss" the backend might yet return, and nothing
 * else. Ordering has to be decided on parsed minutes rather than on the
 * strings: a lexicographic comparison happily reports "25:00" < "26:00" as a
 * usable window, so any non-empty junk in a stored schedule would pass as
 * valid. Seconds are parsed but not compared; a sub-minute window is a dead
 * window, and the editor works in whole minutes.
 */
export const minutesOfDay = (raw: string | null | undefined): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(
    (raw ?? "").trim()
  );
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

export const OVERNIGHT_ERROR =
  "Prekonoćne smene nisu podržane; unesite vreme u okviru istog dana.";

export const validateHoursDraft = (
  draft: HoursDraft
): Partial<Record<VolunteerDayOfWeek, string>> => {
  const errors: Partial<Record<VolunteerDayOfWeek, string>> = {};
  VOLUNTEER_DAYS.forEach((day) => {
    const row = draft[day];
    if (!row.enabled) {
      return;
    }
    if (!row.from || !row.until) {
      errors[day] = "Unesite vreme od i vreme do.";
      return;
    }
    const from = minutesOfDay(row.from);
    const until = minutesOfDay(row.until);
    if (from == null || until == null || from >= until) {
      errors[day] = OVERNIGHT_ERROR;
    }
  });
  return errors;
};

interface WorkingHoursEditorProps {
  draft: HoursDraft;
  onChange: (next: HoursDraft) => void;
  onSave: (entries: VolunteerWorkingHoursEntry[]) => void;
  saving: boolean;
  saveError: string | null;
  savedNotice: boolean;
  /** Card heading. The advisor page sits this editor next to an availability
   *  card that already says "Dostupnost", so it names the editor by its
   *  content instead. */
  title?: string;
  /** Disables only the save button (the rows stay editable): another write
   *  on the hosting page is in flight. */
  locked?: boolean;
  /** Copy overrides for a host whose schedule is not a person's
   *  availability (the association's opening hours). The defaults are the
   *  volunteer and advisor wording. */
  saveLabel?: string;
  hint?: string;
  clearConfirmTitle?: string;
  clearConfirmBody?: string;
  clearConfirmLabel?: string;
}

const WorkingHoursEditor: React.FC<WorkingHoursEditorProps> = ({
  draft,
  onChange,
  onSave,
  saving,
  saveError,
  savedNotice,
  title = "Dostupnost",
  locked = false,
  saveLabel = "Sačuvaj",
  hint = "Dan bez unetog vremena znači da tog dana niste dostupni.",
  clearConfirmTitle = "Uklanjate dostupnost",
  clearConfirmBody = "Uklanjate dostupnost za sve dane, bićete nedostupni.",
  clearConfirmLabel = "Ukloni dostupnost",
}) => {
  const [errors, setErrors] = useState<
    Partial<Record<VolunteerDayOfWeek, string>>
  >({});
  const [confirmingClear, setConfirmingClear] = useState(false);

  const entries = useMemo(() => hoursDraftToEntries(draft), [draft]);

  // Any draft change invalidates shown errors: a user edit is an attempt at a
  // fix, and a refetch (pause toggle, identity save) replaces the values the
  // errors were about.
  useEffect(() => {
    setErrors({});
  }, [draft]);

  const update = (day: VolunteerDayOfWeek, patch: Partial<DayRow>) => {
    onChange({ ...draft, [day]: { ...draft[day], ...patch } });
  };

  const submit = () => {
    const nextErrors = validateHoursDraft(draft);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    // Clearing the whole schedule makes the volunteer permanently
    // unavailable, so it is never a silent side effect of pressing "save".
    if (entries.length === 0) {
      setConfirmingClear(true);
      return;
    }
    onSave(entries);
  };

  return (
    <section className={styles.card} aria-labelledby="volunteer-hours-title">
      <h2 id="volunteer-hours-title" className={styles.cardTitle}>
        {title}
      </h2>
      <p className={styles.hint}>{hint}</p>

      <ul className={styles.dayList}>
        {VOLUNTEER_DAYS.map((day) => {
          const row = draft[day];
          const error = errors[day];
          return (
            <li key={day} className={styles.dayRow}>
              <label className={styles.dayToggle}>
                <input
                  type="checkbox"
                  checked={row.enabled}
                  disabled={saving}
                  onChange={(e) => update(day, { enabled: e.target.checked })}
                />
                <span className={styles.dayName}>
                  {VOLUNTEER_DAY_LABELS[day]}
                </span>
              </label>
              <div className={styles.dayTimes}>
                <label className={styles.timeField}>
                  <span className={styles.timeLabel}>Od</span>
                  <input
                    type="time"
                    value={row.from}
                    disabled={!row.enabled || saving}
                    aria-label={`${VOLUNTEER_DAY_LABELS[day]}, vreme od`}
                    onChange={(e) => update(day, { from: e.target.value })}
                  />
                </label>
                <label className={styles.timeField}>
                  <span className={styles.timeLabel}>Do</span>
                  <input
                    type="time"
                    value={row.until}
                    disabled={!row.enabled || saving}
                    aria-label={`${VOLUNTEER_DAY_LABELS[day]}, vreme do`}
                    onChange={(e) => update(day, { until: e.target.value })}
                  />
                </label>
              </div>
              {error && (
                <p className={styles.fieldError} role="alert">
                  {error}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {saveError && (
        <p className={styles.error} role="alert">
          {saveError}
        </p>
      )}
      {savedNotice && !saveError && (
        <NotificationMessage>Dostupnost je sačuvana.</NotificationMessage>
      )}

      <Button buttonType="blue" onClick={submit} disabled={saving || locked}>
        {saving ? "Čuvanje..." : saveLabel}
      </Button>

      {confirmingClear && (
        <SimpleModal
          show={true}
          className={styles.dialog}
          backdropClickHandler={() => setConfirmingClear(false)}
        >
          <div className={styles.dialogBody}>
            <h3 className={styles.dialogTitle}>{clearConfirmTitle}</h3>
            <p>{clearConfirmBody}</p>
            <div className={styles.dialogActions}>
              <Button
                buttonType="white"
                onClick={() => setConfirmingClear(false)}
              >
                Odustani
              </Button>
              <Button
                buttonType="red"
                onClick={() => {
                  setConfirmingClear(false);
                  onSave([]);
                }}
              >
                {clearConfirmLabel}
              </Button>
            </div>
          </div>
        </SimpleModal>
      )}
    </section>
  );
};

export default WorkingHoursEditor;
